import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import Cart from "../models/cart";
import { onSuccessHandler, onErrorHandler, vatExemptionSuccessHandler } from "./handlers";
import { CartOperation } from '../enums/cartOperation';
import { setBuyerTypeUpdated, startLoading, stopLoading} from './application';
import {config} from "../utils/config";
import { Page } from '../enums/page';
import { discountApi } from './discount';
import { updateGaCartView } from '../components/shopping-bag/ga-module';
import { redirectToURL } from '../utils/url';
import { newCartApi , getCart } from './newCart';

declare const window: any;

export const cartApi = createApi({
    reducerPath: 'cartApi',
    baseQuery: fetchBaseQuery({
        baseUrl: window.Config?.LAMBDA_CART ?? "https://npr-cart.api.pens.com/",
        prepareHeaders: (headers) => {
            headers.set(window.Config?.NP_API_KEY_HEADER_NAME ?? "x-api-key", window.Config?.NP_API_KEY_CLIENT_JS ?? "NSjMi38PBJ6s1rvNMA9FKO6FRn2iDd95bEDoaij8");
            return headers
        },
    }),
    tagTypes: ['Cart'],
    endpoints: (builder) => ({
        getLatestMiniCart: builder.query({
            query: ({ cartId, userId, isLoggedIn }) => ({
                url: `v2/cart/cust/${userId}/mini?isLogged=${isLoggedIn}&locale=${config('locale')}`,
                method: 'GET'
            }),
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try{
                    const {data} = await queryFulfilled;
                    return data;
                }
                catch(error:any){
                    if(error?.error?.data?.data?.statusCode === 404 && error?.error?.data?.message.includes('active cart for the customer')){
                      console.log('Home page redirection due to following error in getLatestMiniCart API ', JSON.stringify(error));
                      redirectToURL(`/${config('locale_details').current_sitename}/`);
                    }
                    else console.log(error);
                }
            }
        }),
        getInvisibleCart: builder.mutation({
          async queryFn({cartId}, {dispatch, getState}, _extraOptions,  baseQuery) 
          {
              const data:any = await baseQuery({ url: `v1/cart/${cartId}?recalc=false&regionCode=${config('country')}&effectivePrice=true&invisible=true`, method: 'GET'});
              return { data: new Cart(data.data.data.cart)};
          },
          async onQueryStarted(args, { dispatch, queryFulfilled }) {
              try{
                  dispatch(startLoading());
                  await queryFulfilled;
              }
              catch(error){
                  console.log(error);
                  dispatch(onErrorHandler({operation: 'getCartByCartIdAndNoRecalc endpoint', err: JSON.stringify(error), errMsg: 'Error: Error in getCartByCartIdAndNoRecalc', api: 'v1/cart/:cartId/'})); 
                  // redirectToURL(`/${config('locale_details').current_sitename}/`);
              }
          }
        }),
        saveCustomField: builder.mutation({
            query: (args: any) => ({
                url : `v1/cart/${args.cartId}/savecustomfield`,
                method: 'POST',
                body: {
                    customFieldName: args.customFieldName,
                    customFieldValue: args.customFieldValue
                }
            }),
            invalidatesTags: ['Cart'],
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try{
                    await queryFulfilled;
                    dispatch(onSuccessHandler(CartOperation.SAVECUSTOMFIELD, {}));
                }
                catch(error){
                    dispatch(onErrorHandler({operation: CartOperation.SAVECUSTOMFIELD, err: JSON.stringify(error), errMsg: 'Error: while saving custom field', api: 'v1/cart/savecustomfield'}));        
                }
            },
        }),
        saveLineItemCustomField: builder.mutation({
            query: (args: any) => ({
                url : `v1/cart/${args.cartId}/savelineitemcustomfield`,
                method: 'POST',
                body: {
                    actionsArr: args.actionsArr
                }
            }),
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                  const { data } = await queryFulfilled;
                  await dispatch(newCartApi.util.invalidateTags(['newCart']));
                  return data;
                } catch (error) {
                  dispatch(
                    onErrorHandler({
                      operation: "saveLineItemCustomField",
                      err: JSON.stringify(error),
                      errMsg: "Error: while saveLineItemCustomField",
                      api: `v1/cart/${args.cartId}/savelineitemcustomfield`,
                    })
                  );
                }
              },
        }),



        naProRatedshipping: builder.mutation({
          query: ({cartId}) => ({
              url : `v1/cart/${cartId}/naproratedshipping`,
              method: 'POST'
          }),
          async onQueryStarted(args, { dispatch, queryFulfilled }) {
              try{
                  const {data} = await queryFulfilled;
                  return data;
              }
              catch(error){
                  console.log(error);
                  dispatch(onErrorHandler({operation:'naProRatedshipping', err: JSON.stringify(error), errMsg: 'Error: while  get cart using naProRatedshipping', api: 'v1/cart/cartId/naproratedshipping'}));
              }
          }
        }),

        getVatExemption: builder.mutation({
            query: ({cartId, selectedCountry, currency, ...params}) => ({
                url : `v2/cart/valuebycountry/${cartId}/${selectedCountry}/${currency}`,
                method: 'GET',
                params: params
            }),
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    const {data} = await queryFulfilled;
                    dispatch(vatExemptionSuccessHandler({cart:data}));
                    return data;
                }
                catch(error){
                    dispatch(onErrorHandler({operation: 'getVATExemption', err: JSON.stringify(error), errMsg: 'Error: while fetching VAT Exemption', api: 'v2/cart/valuebycountry/:cartId/:selectedCountry/:currency'})); 
                }
            },
            transformResponse(response: any){
                return new Cart(response.data);
            }
        }),


        setUserTypeAndAutoApply: builder.mutation({
          async queryFn({isFirstTimeBuyerCart ,  discountCode}, {dispatch, getState}, _extraOptions,  baseQuery) 
          {   
              const {applicationState: {user, buyerTypeUpdated, currentPage}}: any = getState();
              if(!buyerTypeUpdated || (currentPage === Page.CHECKOUTPAYMENT)){
                dispatch(startLoading());
                const data:any = await baseQuery({ url: `v1/cart/${user?.cartId}/savecustomfield`, method: 'POST', body: { cartId: user?.cartId, customFieldName: 'isFirstTimeBuyerCart', customFieldValue: isFirstTimeBuyerCart}});
                if(data?.data){
                  dispatch(setBuyerTypeUpdated(true));
                }
                return data;
              }
              if(currentPage !== Page.CHECKOUTPAYMENT) dispatch(stopLoading());
              return {};
          },
          async onQueryStarted(args, { dispatch, getState, queryFulfilled }) {
              try{
                const {data} = await queryFulfilled;
                const {applicationState: {user, coupon, buyerTypeUpdated, currentPage,isShippingDisc}}: any = getState();
                if(args.discountCode && (coupon.code !== args.discountCode || (currentPage === Page.CHECKOUTPAYMENT))){
                  dispatch(startLoading());
                  await dispatch(discountApi.endpoints.validateCoupon.initiate({ couponCode: args.discountCode , 'locale': config('locale') , callApplyCoupon: true, isAutoApplyCall: true}));
                }else if(!buyerTypeUpdated){
                  dispatch(startLoading());
                  await dispatch(newCartApi.util.invalidateTags(['newCart']));
                }else if(currentPage === Page.SHOPPINGBAG){
                  const cart = getCart(getState() as any, {cartId: user.cartId, userId: user.userId, isLoggedIn: user.isLoggedIn})?.data ?? {};
                  updateGaCartView(cart,coupon,isShippingDisc);
                }else if(currentPage === Page.CHECKOUTPAYMENT && coupon.code === ''){
                  dispatch(stopLoading());
                }
              }
              catch(err:any){
                dispatch(stopLoading());
                if (err?.error?.data?.data?.errorCode == 400 && err?.error?.data?.data?.message.includes('not in active state')) {
                  console.log('Home page redirection due to following error in setUserTypeAndAutoApply ', JSON.stringify(err));
                  redirectToURL(`/${config('locale_details').current_sitename}/`);
                }
                else console.log(err);
              }
          },
        }),

        changeShippingMethodAndGetCart: builder.mutation({
          async queryFn({shippingMethod}, {dispatch, getState}, _extraOptions,  baseQuery) 
          {
              const {applicationState: {user}}: any = getState();
              if (user?.cartId) {
                const data:any = await baseQuery({ url: `v1/cart/${user?.cartId}/savecustomfield`, method: 'POST', body: { cartId: user?.cartId, customFieldName: 'shippingType', customFieldValue: shippingMethod}});
                return data;
              }
              else redirectToURL(`/${config('locale_details').current_sitename}/`);
          },
          async onQueryStarted(args, { dispatch, getState, queryFulfilled }) {
            try{
              const {data} = await queryFulfilled;
              const {applicationState: {user}}: any = getState();
              const cart = JSON.parse(JSON.stringify(getCart(getState() as any, {cartId: user.cartId, userId: user.userId, isLoggedIn: user.isLoggedIn})?.data ?? {}));
              
              const cartResp:any = await dispatch(cartApi.endpoints.naProRatedshipping.initiate({cartId : user?.cartId}));
              const cartObj = new Cart(cartResp.data.data); 
              cartObj.lineItems = cart.lineItems;
              dispatch(newCartApi.util.updateQueryData('getCartByCartId', {cartId: user.cartId, userId: user.userId, isLoggedIn: user.isLoggedIn}, (draft) => Object.assign({}, cartObj)));
              dispatch(stopLoading());
            }
            catch(err: any){
              if (err?.error?.data?.data?.errorCode == 400 && err?.error?.data?.data?.message.includes('not in active state')) {
                console.log('Home page redirection due to following error in changeShippingMethodAndGetCart API ', JSON.stringify(err));
                redirectToURL(`/${config('locale_details').current_sitename}/`);
              }
              else console.log(err);
            }
          }
        }),

    }),
})

export const { 
    useGetLatestMiniCartQuery, 
    useGetInvisibleCartMutation,
    useNaProRatedshippingMutation,
    useGetVatExemptionMutation,
    useSaveCustomFieldMutation,
    useSaveLineItemCustomFieldMutation,
    useSetUserTypeAndAutoApplyMutation,
    useChangeShippingMethodAndGetCartMutation,
} = cartApi;

